
import { ControlConditions, ControlConditionSingle } from "@/types/controls";
import Vue from "vue";
import { Component, Inject, Prop } from "vue-property-decorator";
import { ElementStudioContext } from "./ElementStudioTabsAndPanels.vue";
import { getElementControlByElementControlPathWithPrefix } from "@/lib/elements/utils";
import Repeater from "@/components/Reusable/CustomInputs/Repeater/Repeater.vue";
import defensiveCopy from "@/util/defensive-copy";

@Component({
  components: { Repeater }
})
export default class ElementStudioConditionInput extends Vue {
  @Prop()
  value!: ControlConditions | undefined;

  @Prop({ default: "Condition" })
  title!: string;

  @Prop({ default: "Only Show Control When Condition Is Met" })
  label!: string;

  @Prop()
  pathStartsWith!: string;

  @Inject("context") readonly context!: ElementStudioContext;

  mounted() {
    // we want to transition away from single-conditions
    if (this.value && !Array.isArray(this.value)) {
      this.emit([[this.value]]);
    }
  }

  getBlankCondition() {
    return {
      path: "",
      operand: "equals" as const,
      value: ""
    };
  }

  getOperands(condition: ControlConditionSingle) {
    const path = condition.path;

    if (!path) {
      return [];
    }

    const control = this.getControl(path);

    let operands = [
      "equals",
      "not equals",
      "is one of",
      "is none of",
      "is set",
      "is not set",
      "contains",
      "does not contain"
    ];

    if (control?.options.type === "multiselect") {
      operands = ["includes"];
    }

    if (control?.options.type === "toggle") {
      operands = ["is set", "is not set"];
    }

    return operands.map(x => {
      return { text: x, value: x };
    });
  }

  hasMultipleValues(condition: ControlConditionSingle) {
    return Array.isArray(condition.value) ? condition.value : [];
  }

  get internalValue() {
    if (!this.value) return [];

    // We update the value when mounted to transition away from single conditions
    // But still need to add safety here for types and to avoid any potential bug
    if (!Array.isArray(this.value)) return [[this.value]];

    return this.value;
  }

  get paths() {
    const paths = this.context
      .getPathsToAllControls()
      .filter(path => !path.includes("settings.animations"));

    if (this.pathStartsWith) {
      return paths
        .filter(path => path.startsWith(this.pathStartsWith))
        .map(path => path.replace(this.pathStartsWith, ""));
    }

    return paths;
  }

  getPossibleValues(condition: ControlConditionSingle) {
    const path = condition.path;
    if (!path) return [];

    const control = this.getControl(path);

    if (!control) return [];

    const result = control.options.items?.map(x => String(x.value));

    if (!result) return [];

    return result;
  }

  getControl(path: string) {
    return getElementControlByElementControlPathWithPrefix(
      this.context.element(),
      path
    );
  }

  updatePath(path: string, index1: number, index2: number) {
    const internalValue = defensiveCopy(this.internalValue);

    internalValue[index1][index2].path = path;

    this.emit(internalValue);
  }

  updateOperand(
    operand:
      | "equals"
      | "not equals"
      | "is one of"
      | "is set"
      | "contains"
      | "does not contain",
    index1: number,
    index2: number
  ) {
    const internalValue = defensiveCopy(this.internalValue);

    internalValue[index1][index2].operand = operand;

    this.emit(internalValue);
  }

  updateValue(newValue: string, index1: number, index2: number) {
    const internalValue = defensiveCopy(this.internalValue);

    internalValue[index1][index2].value = newValue;

    this.emit(internalValue);
  }

  updateOrCondition(conditions: ControlConditionSingle[], index: number) {
    const internalValue = defensiveCopy(this.internalValue);

    internalValue[index] = conditions;

    this.emit(internalValue);
  }

  emit(condition: ControlConditions) {
    this.$emit("input", condition);
  }
}
